import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/* ------------------------------------------------------------------ */
/*  Shared palette                                                     */
/* ------------------------------------------------------------------ */
const DEFAULT_PALETTE = [
  '#ef4444', '#f97316', '#f59e0b', '#facc15', '#84cc16',
  '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899',
];

function getTextColor(hex) {
  try {
    const c = (hex || '').replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#000' : '#fff';
  } catch {
    return '#fff';
  }
}

/* ------------------------------------------------------------------ */
/*  SimpleBarChart                                                     */
/* ------------------------------------------------------------------ */
export function SimpleBarChart({ data, dataKey, color = null, maxValue }) {
  const [animated, setAnimated] = useState(false);
  const max = maxValue || Math.max(...data.map((item) => item[dataKey] || 0), 1);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const bg =
          item.color ||
          (Array.isArray(color)
            ? color[index] || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length]
            : color || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length]);
        const textColor = getTextColor(bg);
        return (
          <div
            key={index}
            className="flex items-center gap-3 animate-fade-in-up"
            style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
          >
            <div className="w-20 text-sm font-medium text-gray-700 text-right">
              {item.name || item.ageGroup || item.municipality || item.reach}
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden shadow-inner">
              <div
                className="h-6 rounded-full progress-bar flex items-center px-3 shadow-sm"
                style={{
                  width: animated ? `${(item[dataKey] / max) * 100}%` : '0%',
                  backgroundColor: bg,
                  color: textColor,
                  transition: `width 1s ease-out ${index * 0.05}s`,
                }}
              >
                <span className="text-xs font-semibold">{item[dataKey]}</span>
              </div>
            </div>
            <div className="w-12 text-sm font-bold text-gray-800">{item[dataKey]}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SimpleLineChart                                                    */
/* ------------------------------------------------------------------ */
export function SimpleLineChart({ data, dataKey = 'value', color = null, height = 160 }) {
  const points = (data || []).map((d) => ({
    label: d.name || d.label || d.reach,
    value: d[dataKey] || 0,
    color: d.color,
  }));
  const n = points.length || 1;
  const max = Math.max(...points.map((p) => p.value), 1);
  const w = 300;
  const h = height;
  const pad = 28;

  const coords = points.map((p, i) => {
    const x = pad + (i / Math.max(n - 1, 1)) * (w - pad * 2);
    const y = h - pad - (p.value / max) * (h - pad * 2);
    return { x, y, ...p };
  });

  const strokeColor = color || (points[0] && points[0].color) || '#92400e';
  const polyPoints = coords.map((c) => `${c.x},${c.y}`).join(' ');

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line
            key={i}
            x1={pad}
            x2={w - pad}
            y1={pad + t * (h - pad * 2)}
            y2={pad + t * (h - pad * 2)}
            stroke="#eee"
            strokeWidth="1"
          />
        ))}
        <polygon
          points={[`${pad},${h - pad}`, ...coords.map((c) => `${c.x},${c.y}`), `${w - pad},${h - pad}`].join(' ')}
          fill={strokeColor}
          opacity="0.06"
        />
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={polyPoints}
        />
        {coords.map((c, i) => (
          <g key={i}>
            <circle cx={c.x} cy={c.y} r="4" fill={c.color || strokeColor} stroke="#fff" strokeWidth="1" />
          </g>
        ))}
      </svg>
      <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-700">
        {coords.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color || strokeColor }} />
            <div className="truncate">{c.label}</div>
            <div className="ml-auto font-mono text-sm">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SimplePieChart                                                     */
/* ------------------------------------------------------------------ */
export function SimplePieChart({ data, title }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }} />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">{item.name}</div>
              <div className="text-xs text-gray-500">
                {item.value} ({((item.value / total) * 100).toFixed(1)}%)
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-6">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            {data.map((item, index) => {
              const startAngle = data.slice(0, index).reduce((sum, d) => sum + (d.value / total) * 360, 0);
              const endAngle = startAngle + (item.value / total) * 360;
              const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
              const startX = 50 + 35 * Math.cos((startAngle * Math.PI) / 180);
              const startY = 50 + 35 * Math.sin((startAngle * Math.PI) / 180);
              const endX = 50 + 35 * Math.cos((endAngle * Math.PI) / 180);
              const endY = 50 + 35 * Math.sin((endAngle * Math.PI) / 180);
              return (
                <path
                  key={index}
                  d={`M 50 50 L ${startX} ${startY} A 35 35 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
                  fill={item.color}
                  opacity="0.8"
                />
              );
            })}
            <circle cx="50" cy="50" r="20" fill="white" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-sm font-bold text-gray-700">{total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SimpleStackedBar                                                   */
/* ------------------------------------------------------------------ */
export function SimpleStackedBar({ segments }) {
  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0) || 1;
  return (
    <div className="space-y-4">
      <div className="w-full bg-gray-200 rounded h-6 overflow-hidden">
        <div className="h-6 flex" style={{ width: '100%' }}>
          {segments.map((seg, idx) => {
            const bg = seg.color || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
            return (
              <div
                key={idx}
                title={`${seg.name}: ${seg.value} (${((seg.value / total) * 100).toFixed(1)}%)`}
                style={{
                  width: `${(seg.value / total) * 100}%`,
                  backgroundColor: bg,
                  minWidth: seg.value > 0 ? '2px' : '0',
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {segments.map((seg, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
            <div className="text-sm text-gray-700">{seg.name}</div>
            <div className="ml-auto text-sm font-medium text-gray-700">{seg.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SimpleFunnelChart                                                  */
/* ------------------------------------------------------------------ */
export function SimpleFunnelChart({ items, labelKey = 'municipality', valueKey = 'value' }) {
  const data = (items || [])
    .map((it) => ({
      label: it[labelKey] || it.municipality || 'Unknown',
      value: it[valueKey] !== undefined ? it[valueKey] : (it.farmers || 0) + (it.processors || 0),
      farmers: it.farmers || 0,
      processors: it.processors || 0,
    }))
    .sort((a, b) => b.value - a.value);

  const max = Math.max(...data.map((d) => d.value), 1);
  const colors = ['#92400e', '#d97706', '#059669', '#0369a1', '#7c3aed', '#e11d48', '#0ea5a4', '#f59e0b'];

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-3">
        {data.map((d, i) => {
          const widthPct = Math.max((d.value / max) * 100, 12);
          return (
            <div key={i} className="w-full flex items-center">
              <div className="flex-1 flex justify-center">
                <div
                  className="rounded-md text-white text-xs font-medium flex items-center justify-center"
                  style={{ width: `${widthPct}%`, backgroundColor: colors[i % colors.length], height: 36 }}
                  title={`${d.label}: ${d.value} (Farmers: ${d.farmers}, Processors: ${d.processors})`}
                >
                  <span className="px-2 truncate">{d.label}</span>
                </div>
              </div>
              <div className="w-24 text-right text-sm font-medium text-gray-700 pl-3">{d.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors[i % colors.length] }} />
            <div className="flex-1 truncate">{d.label}</div>
            <div className="font-mono text-sm">{d.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SimpleBubbleChart                                                  */
/* ------------------------------------------------------------------ */
export function SimpleBubbleChart({ data = [], dataKey = 'production', maxRadius = 40, palette = null }) {
  const items = (data || []).slice(0, 50);
  const max = Math.max(...items.map((i) => i[dataKey] || 0), 1);

  const getRadius = (val) => {
    const ratio = Math.sqrt((val || 0) / max);
    return Math.max(6, Math.round(ratio * maxRadius));
  };

  const getColor = (idx) =>
    Array.isArray(palette)
      ? palette[idx] || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length]
      : palette || DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4 items-center justify-start">
        {items.map((it, idx) => {
          const val = it[dataKey] || 0;
          const r = getRadius(val);
          const color = getColor(idx);
          const textColor = getTextColor(color);
          const label = it.name || it.label || `Item ${idx + 1}`;
          return (
            <div key={idx} className="flex flex-col items-center" style={{ width: Math.max(r * 1.4, 80) }}>
              <div
                className="rounded-full flex items-center justify-center"
                style={{
                  width: r * 2,
                  height: r * 2,
                  backgroundColor: color,
                  color: textColor,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }}
                title={`${label}: ${val}`}
              >
                <div className="text-xs font-medium truncate" style={{ maxWidth: r * 1.6 }}>
                  {val}
                </div>
              </div>
              <div
                className="mt-2 text-xs text-gray-700 text-center truncate"
                style={{ maxWidth: Math.max(r * 1.6, 72) }}
              >
                {label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const chartDataShape = PropTypes.arrayOf(PropTypes.object);

SimpleBarChart.propTypes = {
  data: chartDataShape.isRequired,
  dataKey: PropTypes.string.isRequired,
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  maxValue: PropTypes.number,
};

SimpleLineChart.propTypes = {
  data: chartDataShape,
  dataKey: PropTypes.string,
  color: PropTypes.string,
  height: PropTypes.number,
};

SimplePieChart.propTypes = {
  data: chartDataShape.isRequired,
  title: PropTypes.string,
};

SimpleStackedBar.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, value: PropTypes.number, color: PropTypes.string }),
  ).isRequired,
};

SimpleFunnelChart.propTypes = {
  items: chartDataShape,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
};

SimpleBubbleChart.propTypes = {
  data: chartDataShape,
  dataKey: PropTypes.string,
  maxRadius: PropTypes.number,
  palette: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};
